// -----------------------------------------------------------
// FloraIQ - LinkCloudToTreesModal — Modal Vincular Nuvem a Árvores
// -----------------------------------------------------------

import { useState } from "react";
import { surveys } from "@/mocks/pointcloud";

interface LinkCloudToTreesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function LinkCloudToTreesModal({ isOpen, onClose }: LinkCloudToTreesModalProps) {
  const [selectedSurvey, setSelectedSurvey] = useState("");
  const [method, setMethod] = useState("proximidade");
  const [simulating, setSimulating] = useState(false);
  const [simulatedCount, setSimulatedCount] = useState<number | null>(null);

  if (!isOpen) return null;

  const handleSimulate = () => {
    setSimulating(true);
    setSimulatedCount(null);
    setTimeout(() => {
      setSimulating(false);
      setSimulatedCount(Math.floor(Math.random() * 500) + 200);
    }, 1500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-white rounded-card w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto fade-in">
        <div className="sticky top-0 bg-white border-b border-background-200/70 px-5 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-foreground-900 font-heading">Vincular Nuvem a Árvores</h3>
            <p className="text-xs text-foreground-500 mt-0.5">Associe pontos da nuvem às árvores do CTA</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer" aria-label="Fechar">
            <i className="ri-close-line text-foreground-600"></i>
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1">Selecionar levantamento <span className="text-red-500">*</span></label>
            <select
              value={selectedSurvey}
              onChange={(e) => setSelectedSurvey(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent bg-white cursor-pointer"
            >
              <option value="">Selecionar...</option>
              {surveys.filter((s) => s.temNuvem).map((s) => (
                <option key={s.id} value={s.id}>{s.nome}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1">Método de vínculo</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: "proximidade", label: "Por proximidade", icon: "ri-pin-distance-line" },
                { id: "copa", label: "Por copa estimada", icon: "ri-bubble-chart-line" },
                { id: "manual", label: "Seleção manual", icon: "ri-cursor-line" },
                { id: "cta", label: "Por código CTA", icon: "ri-barcode-line" },
              ].map((m) => (
                <button
                  key={m.id}
                  onClick={() => setMethod(m.id)}
                  className={`flex items-center gap-2 px-3 py-2.5 text-xs rounded-md border transition-colors cursor-pointer ${
                    method === m.id
                      ? "border-primary-500 bg-primary-50 text-primary-700"
                      : "border-background-200/70 text-foreground-600 hover:bg-background-50"
                  }`}
                >
                  <i className={`${m.icon} text-sm`}></i>
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {method === "proximidade" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">Distância máxima (m)</label>
                <input type="number" defaultValue={2.0} step={0.5} className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent bg-white" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">Altura mínima (m)</label>
                <input type="number" defaultValue={2.0} step={0.5} className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent bg-white" />
              </div>
            </div>
          )}

          {simulatedCount !== null && (
            <div className="bg-green-50 border border-green-200/60 rounded-lg p-3 slide-up">
              <div className="flex items-center gap-2">
                <i className="ri-check-double-line text-green-600"></i>
                <p className="text-sm text-green-700">
                  Simulação concluída! <strong>{simulatedCount} árvores</strong> vinculadas com sucesso.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-background-200/70 px-5 py-3 flex items-center justify-between">
          <button onClick={onClose} className="btn-outline text-xs px-4 py-2 cursor-pointer whitespace-nowrap">Cancelar</button>
          <button
            onClick={handleSimulate}
            disabled={!selectedSurvey || simulating}
            className={`btn-primary text-xs px-4 py-2 cursor-pointer whitespace-nowrap ${
              !selectedSurvey || simulating ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {simulating ? (
              <>
                <i className="ri-loader-4-line animate-spin mr-1"></i>Simulando...
              </>
            ) : (
              <>
                <i className="ri-link mr-1"></i>Simular vínculo
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}